import React, { forwardRef } from 'react';
import ChatMessage from '../ChatMessage';
import { MessagesContainer, DateDivider } from '../styled/index';
import { highlightText } from '../utils/searchUtils';

const MessageList = forwardRef(({
  messages,
  searchText,
  searchResults,
  currentResultIndex,
  currentUser,
  conversationUsers,
  conversationId,
  onFlagMessage,
  onDeleteMessage
}, containerRef) => {

  // Function to group messages by date
  const groupMessagesByDate = (messages) => {
    if (!messages || !messages.length) return {};

    return messages.reduce((groups, message) => {
      const date = new Date(message.time).toLocaleDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
      return groups;
    }, {});
  };

  const renderMessages = () => {
    if (!messages) return null;

    // Apply translations if available
    const translatedMessages = messages.map(msg => {
      if (msg.translatedMessages && msg.type === 'user') {
        return {
          ...msg,
          content: msg.translatedMessages.content
        }
      } else {
        return {
          ...msg,
          content: msg.content
        }
      }
    });

    // Group messages by date
    const groupedMessages = groupMessagesByDate(translatedMessages);
    const renderedElements = [];

    Object.entries(groupedMessages).forEach(([date, dateMessages]) => {
      // Add date divider
      renderedElements.push(
        <DateDivider key={`date-${date}`}>
          <span>{new Date(date).toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}</span>
        </DateDivider>
      );

      // Add messages for this date
      dateMessages.forEach((msg, messageIndex) => {
        // Find the original index in the full message array for search functionality
        const originalIndex = translatedMessages.findIndex(m => m === msg);
        
        // Check if this message is in search results
        const isSearchResult = searchText && searchText.trim() !== '' && searchResults.includes(originalIndex);
        const isActiveResult = isSearchResult && searchResults[currentResultIndex] === originalIndex;

        // Enhanced style for better visibility
        let style = {
          position: 'relative',
          padding: '2px 5px',
          margin: '4px 0',
          borderRadius: '4px',
          lineHeight: '1.5'
        };

        // Apply highlighting styles for search results
        if (isActiveResult) {
          style.backgroundColor = 'rgba(0, 108, 219, 0.08)';
          style.borderLeft = '4px solid rgba(0, 108, 219, 0.8)';
          style.paddingLeft = '5px';
          style.marginLeft = '-5px';
          style.boxShadow = '0 0 8px rgba(0, 108, 219, 0.1)';
        } else if (isSearchResult) {
          style.borderLeft = '3px solid rgba(0, 108, 219, 0.4)';
          style.paddingLeft = '5px';
          style.marginLeft = '-5px';
        }

        // Create modified message with highlighted content if needed
        let modifiedMessage;
        if (msg.isHTML) {
          modifiedMessage = { ...msg };
        } else {
          modifiedMessage = {
            ...msg,
            content: searchText && searchText.trim() !== '' 
              ? highlightText(msg.content, searchText) 
              : msg.content
          };
        }

        renderedElements.push(
          <div
            key={`message-${date}-${messageIndex}`}
            className="message-item"
            style={style}
            id={`message-${originalIndex}`}
            data-search-result={isSearchResult ? 'true' : 'false'}
            data-active-result={isActiveResult ? 'true' : 'false'}
          >
            {onDeleteMessage && (
              <button
                title="Delete this message from the feed"
                onClick={(e) => { e.stopPropagation(); onDeleteMessage(originalIndex, msg.type); }}
                onMouseOver={(e) => { e.currentTarget.style.opacity = 1; }}
                onMouseOut={(e) => { e.currentTarget.style.opacity = 0.35; }}
                style={{
                  position: 'absolute', top: 2, right: 2, zIndex: 5,
                  width: 22, height: 22, borderRadius: 6, cursor: 'pointer',
                  background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.3)',
                  color: '#ef4444', fontSize: 11, lineHeight: 1, opacity: 0.35,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                🗑
              </button>
            )}
            <ChatMessage
              message={modifiedMessage}
              type={msg.type}
              timestamp={msg.time}
              currentUser={currentUser}
              conversationUsers={conversationUsers}
              messageIndex={originalIndex}
              conversationId={conversationId}
              onFlagMessage={onFlagMessage}
            />
          </div>
        );
      });
    });

    return renderedElements;
  };

  return (
    <MessagesContainer ref={containerRef}>
      {renderMessages()}
    </MessagesContainer>
  );
});

MessageList.displayName = 'MessageList';

export default MessageList; 